/**
 * "Mascot animations" (owner request 2026-10-11): one native setting in the desktop agent (UserPrefs, on by default),
 * shown in Settings › General › Appearance and in the mascot's right-click menu, always in step. The widget never keeps
 * a copy of its own: the desktop shell relays the agent's snapshot as the `gyro:mascot-animations` DOM event, the widget
 * asks for it with `gyro:mascot-animations-ready` once the control mounts, and asks for a change with
 * `gyro:mascot-animations-set` ({ enabled: boolean }); the agent answers every change with a new snapshot. On every
 * other surface (the web widget, the Office pane) the host never answers, and the control is not shown.
 */
export const MASCOT_ANIMATIONS_EVENT = 'gyro:mascot-animations';
export const MASCOT_ANIMATIONS_READY_EVENT = 'gyro:mascot-animations-ready';
export const MASCOT_ANIMATIONS_SET_EVENT = 'gyro:mascot-animations-set';
export const MASCOT_ANIMATIONS_LABEL = 'Mascot animations';
export const MASCOT_ANIMATIONS_DESCRIPTION = 'Play an action when you click a desktop mascot.';
/** How long the control waits for the desktop agent's first snapshot before it decides the host has no such setting. */
export const MASCOT_ANIMATIONS_WAIT_MS = 5_000;
const SUPPRESSIONS = new Set(['off', 'os-reduced-motion', 'app-reduced-motion', 'no-action']);
/** A deliberate native withdrawal, distinct from an invalid or incomplete message. */
export function isMascotAnimationsUnavailable(value) {
    return value !== null && typeof value === 'object' && !Array.isArray(value)
        && Object.keys(value).length === 1 && Object.hasOwn(value, 'available')
        && value.available === false;
}
/** The agent's snapshot, exactly: every field of its kind, nothing else read. Null for anything else. */
export function parseMascotAnimationsSnapshot(value) {
    if (!value || typeof value !== 'object' || Array.isArray(value))
        return null;
    const record = value;
    if (record.available !== true || typeof record.enabled !== 'boolean' || typeof record.persisted !== 'boolean' || typeof record.playable !== 'boolean')
        return null;
    const suppressed = record.suppressed;
    if (!(suppressed === null || (typeof suppressed === 'string' && SUPPRESSIONS.has(suppressed))))
        return null;
    return { available: true, enabled: record.enabled, persisted: record.persisted, playable: record.playable, suppressed: suppressed };
}
/**
 * What Settings says under the switch: why a click does not play while the setting is on (the saved choice is kept), or
 * that the last save failed. Null when there is nothing to explain.
 */
export function mascotAnimationsExplanation(snapshot) {
    if (!snapshot.persisted)
        return 'Your choice could not be saved on this computer. It applies until Gyro restarts.';
    if (!snapshot.enabled)
        return null;
    switch (snapshot.suppressed) {
        case 'os-reduced-motion': return 'Paused while Windows animation effects are off (Settings › Accessibility › Visual effects). Your choice is kept.';
        case 'app-reduced-motion': return 'Paused while Motion is set to Reduced above. Your choice is kept.';
        case 'no-action': return 'The outfit you are wearing has no action yet. Your choice is kept for outfits that do.';
        default: return null;
    }
}
//# sourceMappingURL=mascotAnimations.js.map